import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AbilityContext, Can } from "@/casl.config";
import { useAbility } from "@casl/react";
import BaseTableV2 from "@reusables/BaseTableV2";
import BaseBadge from "@reusables/BaseBadge";
import BaseSyncedFromSourcesBadge from "@reusables/BaseSyncedFromSourcesBadge";
import { Category } from "@/types/general";
import { ReactComponent as EditSVG } from "@assets/icons/ic_edit.svg";
import { ReactComponent as DeleteSVG } from "@assets/icons/ic_trash.svg";
import { Collapse, Skeleton } from "@mui/material";
import {
    createColumnHelper,
    ExpandedState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { cn } from "@helpers/utils";
import { ChevronDown } from "lucide-react";
import { ReactComponent as FolderAddSVG } from "@assets/icons/ic_folder_add.svg";

type RecursiveCategoriesTableProps = {
    data: Category.Root[];
    depth?: number;
    parentId?: number | null;
    isDataLoading?: boolean;
    onEdit: (category: Category.Root, parentId?: number | null) => void;
    onDelete: (category: Category.Root, parentId?: number | null) => void;
    onCreateChild: (parentId: number) => void;
};

export default function RecursiveCategoriesTable({
    data,
    depth = 0,
    parentId = null,
    isDataLoading = false,
    onEdit,
    onDelete,
    onCreateChild,
}: RecursiveCategoriesTableProps) {
    const { t } = useTranslation("", { keyPrefix: "settings.general.categories" });
    const ability = useAbility(AbilityContext);

    // Hide headers for nested tables to match the spec.
    const hideHeader = depth > 0;
    // Local expanded state per nesting level.
    const [expanded, setExpanded] = useState<ExpandedState>({});
    // Column helper is memoized for stable column definitions.
    const columnHelper = useMemo(() => createColumnHelper<Category.Root>(), []);
    // Indentation per depth for the identifier column.
    const indentOffset = depth * 16;

    const columns = useMemo(() => {
        // Prevent row toggle when clicking actions/empty chevron area.
        const stopRowToggle = (event: React.MouseEvent) => event.stopPropagation();

        return [
            columnHelper.display({
                id: "code",
                header: t("table.columns.0"),
                cell: (info) => {
                    const row = info.row.original;
                    const canExpand = info.row.getCanExpand();
                    return (
                        <div
                            className="flex flex-row items-center space-x-2"
                            style={{ paddingLeft: indentOffset }}
                            onClick={canExpand ? undefined : stopRowToggle}
                        >
                            {/* Fixed chevron slot to keep column alignment */}
                            <div className="w-[16px] h-[9px] flex items-center justify-center text-secondary-gray">
                                {canExpand ? (
                                    <ChevronDown
                                        size={18}
                                        style={{
                                            transform: info.row.getIsExpanded()
                                                ? "rotate(0deg)"
                                                : "rotate(-90deg)",
                                            transition: "transform 200ms ease",
                                        }}
                                    />
                                ) : null}
                            </div>
                            <div className="font-thin text-secondary-gray">{row.code}</div>
                            <BaseSyncedFromSourcesBadge
                                integrations={[
                                    {
                                        slug: "twenty_four_seven_office",
                                        integrated: () => !!row?.twenty_four_seven_office_id,
                                    },
                                ]}
                            />
                        </div>
                    );
                },
                maxSize: 220,
            }),
            columnHelper.display({
                id: "name",
                header: t("table.columns.1"),
                cell: (info) => (
                    <div className={depth === 0 ? "bold-highlight" : undefined}>
                        {info.row.original.name}
                    </div>
                ),
            }),
            columnHelper.display({
                id: "is_service",
                header: t("table.columns.2"),
                cell: (info) =>
                    info.row.original.is_service ? (
                        <BaseBadge className="bg-light-green">{t("table.isService")}</BaseBadge>
                    ) : (
                        <></>
                    ),
                maxSize: 140,
            }),
            columnHelper.display({
                id: "actions",
                header: "",
                cell: (info) =>
                    ability.can("categories.edit", "general") ? (
                        <div className="text-tables-secondaryIcon" onClick={stopRowToggle}>
                            <FolderAddSVG
                                className="mr-[12px] cursor-pointer hover:text-tables-highlightedIcon"
                                onClick={() => onCreateChild(info.row.original.id)}
                            />
                            <EditSVG
                                className="mr-[8px] cursor-pointer hover:text-tables-highlightedIcon"
                                onClick={() => onEdit(info.row.original, parentId)}
                            />
                            <DeleteSVG
                                className="cursor-pointer hover:text-tables-highlightedIcon"
                                onClick={() => onDelete(info.row.original, parentId)}
                            />
                        </div>
                    ) : null,
                maxSize: 120,
            }),
        ];
    }, [ability, columnHelper, depth, indentOffset, onCreateChild, onDelete, onEdit, parentId, t]);

    const table = useReactTable({
        data,
        columns,
        state: {
            expanded,
        },
        onExpandedChange: setExpanded,
        getRowId: (row) => row.id.toString(),
        getRowCanExpand: (row) => (row.original.children?.length ?? 0) > 0,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });

    // Skeleton rows for loading state (header still visible).
    const skeletonRows = Array.from({ length: 5 }, (_, index) => index);

    return (
        <BaseTableV2>
            {!hideHeader && (
                <>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <BaseTableV2.Header key={headerGroup.id} className="mb-[15px]">
                            <BaseTableV2.Row>
                                {headerGroup.headers.map((header) => (
                                    <BaseTableV2.Cell key={header.id} {...header.column.columnDef}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef.header,
                                                  header.getContext()
                                              )}
                                    </BaseTableV2.Cell>
                                ))}
                            </BaseTableV2.Row>
                        </BaseTableV2.Header>
                    ))}
                </>
            )}
            <BaseTableV2.Body className="space-y-[15px]">
                {isDataLoading
                    ? skeletonRows.map((index) => (
                          <BaseTableV2.Row key={`sk_${index}`} className="rounded-[8px]">
                              {/* Render skeletons per column to preserve layout */}
                              {columns.map((column) => (
                                  <BaseTableV2.Cell
                                      key={`${column.id ?? "col"}_${index}`}
                                      {...column}
                                  >
                                      <Skeleton variant="text" height={16} />
                                  </BaseTableV2.Cell>
                              ))}
                          </BaseTableV2.Row>
                      ))
                    : table.getRowModel().rows.map((row) => (
                          <div key={row.id}>
                              <BaseTableV2.Row
                                  className={cn(
                                      row.getCanExpand() && "cursor-pointer",
                                      "hover:bg-[#ECEDFE]",
                                      row.getIsExpanded() && "bg-[#ECEDFE]",
                                      "rounded-[8px]"
                                  )}
                                  onClick={() => {
                                      if (row.getCanExpand()) {
                                          row.toggleExpanded();
                                      }
                                  }}
                              >
                                  {row.getVisibleCells().map((cell) => (
                                      <BaseTableV2.Cell key={cell.id} {...cell.column.columnDef}>
                                          {flexRender(
                                              cell.column.columnDef.cell,
                                              cell.getContext()
                                          )}
                                      </BaseTableV2.Cell>
                                  ))}
                              </BaseTableV2.Row>
                              {row.original.children?.length ? (
                                  <Collapse in={row.getIsExpanded()} timeout="auto" unmountOnExit>
                                      {/* Nested table rendered inside collapse */}
                                      <div className="pt-[15px]">
                                          <RecursiveCategoriesTable
                                              data={row.original.children}
                                              depth={depth + 1}
                                              parentId={row.original.id}
                                              onEdit={onEdit}
                                              onDelete={onDelete}
                                              onCreateChild={onCreateChild}
                                              isDataLoading={isDataLoading}
                                          />
                                      </div>
                                  </Collapse>
                              ) : null}
                          </div>
                      ))}
            </BaseTableV2.Body>
        </BaseTableV2>
    );
}
